import { useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  CAlert,
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CFormInput,
  CFormLabel,
  CInputGroup,
  CInputGroupText,
  CListGroup,
  CListGroupItem,
  CRow,
  CSpinner,
  CStepper,
} from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import { cilLockLocked } from "@coreui/icons";
import type { ApiError } from "../../api/client";
import { useCustomerProfile } from "../../api/hooks/useCustomerProfile";
import { useMyMemberships } from "../../api/hooks/useMemberships";
import { useMyMembershipRequests, useRequestMembership } from "../../api/hooks/useMembershipRequests";
import { isExternalTier, useMembershipTiers } from "../../api/hooks/useMembershipTiers";
import { MAX_PLATFORM_ACCOUNT_LENGTH, usePlatformAccounts } from "../../api/hooks/usePlatformAccounts";
import { useFailure } from "../../context/useFailure";
import { useToast } from "../../context/useToast";
import { membershipDetailsPath, routePaths } from "../../routes/route-paths";
import { MembershipBadge } from "../trading-strategies/components/MembershipBadge";
import { tierStandings } from "./membership-state";
import { useTradingStrategies } from "../../api/hooks/useTradingStrategies";
import { countStrategiesByIds } from "../trading-strategies/strategySides";

// Mirrors CStepper's exposed imperative API, as ProfileEditWizard does.
interface CStepperRef {
  next: () => void;
  prev: () => void;
  finish: () => void;
  reset: () => void;
}

/**
 * Telling us about a membership held on another platform: which one, the account it's under, then send.
 * Staff check it there and approve or decline it in the back office.
 */
export function RequestMembershipWizard() {
  const { t } = useTranslation("memberships");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showSuccess, showWarning } = useToast();
  const { showFailure, clearFailure } = useFailure();
  const tiers = useMembershipTiers();
  const catalogue = useTradingStrategies().data ?? [];
  const memberships = useMyMemberships();
  const requests = useMyMembershipRequests();
  const accounts = usePlatformAccounts();
  const profile = useCustomerProfile();
  const requestMembership = useRequestMembership();

  const stepperRef = useRef<CStepperRef>(null);
  const [, setCurrentStep] = useState(1);
  // Read once, from Browse memberships or Send again; picking another tier afterwards wins.
  const [tierId, setTierId] = useState(() => searchParams.get("tier") ?? "");
  // What the customer typed, by platform. Until they type, the account on their profile.
  const [typed, setTyped] = useState<Record<string, string>>({});

  const isLoading = tiers.isLoading || memberships.isLoading || requests.isLoading || accounts.isLoading;
  const loadError = tiers.error ?? memberships.error ?? requests.error ?? accounts.error;

  const standingOf = tierStandings(memberships.data ?? [], requests.data ?? []);
  // A tier they hold, or are waiting to hear about, can't be asked for again; a declined one can.
  const available = (tiers.data ?? [])
    .filter(isExternalTier)
    .filter((tier) => {
      const standing = standingOf(tier.id);
      return standing === "none" || standing === "declined";
    });
  const tier = available.find((candidate) => candidate.id === tierId) ?? null;

  const platformAccount = tier ? accounts.data?.find((account) => account.platformId === tier.platform.id) : undefined;
  const savedAccount = platformAccount?.account ?? "";
  // A locked account is the one we are checking, or have checked, for a membership, so it is the only one to send.
  const locked = platformAccount?.locked ?? false;
  const account = tier && !locked ? (typed[tier.platform.id] ?? savedAccount) : savedAccount;
  const trimmed = account.trim();
  const accountReady = trimmed.length > 0 && trimmed.length <= MAX_PLATFORM_ACCOUNT_LENGTH;
  const accountLabel = tier?.platform.accountPrompt ?? t("wizard.account.label", { platform: tier?.platform.name });
  // Only offered where the platform asks for an email; X asks for a handle.
  const asksForEmail = /email/i.test(tier?.platform.accountPrompt ?? "");
  const signInEmail = profile.data?.email ?? null;
  const offerSignInEmail = !locked && asksForEmail && signInEmail !== null && trimmed !== signInEmail;

  const handleSend = async () => {
    if (!tier) {
      return;
    }

    clearFailure();
    try {
      await requestMembership.mutateAsync({ membershipTierId: tier.id, platformAccount: trimmed });
      showSuccess(t("wizard.sent", { tier: tier.name, platform: tier.platform.name }));
      navigate(membershipDetailsPath(tier.id));
    } catch (err) {
      const apiError = err as ApiError;
      const message = err instanceof Error ? err.message : t("wizard.sendError");

      if (apiError?.status !== undefined && apiError.status < 500) {
        showWarning(message);
      } else {
        showFailure(message, apiError?.traceId);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <CSpinner color="primary" />
      </div>
    );
  }

  if (loadError) {
    return <CAlert color="danger">{t("wizard.loadError")}</CAlert>;
  }

  if (available.length === 0) {
    return (
      <CAlert color="info">
        {t("wizard.membership.nothingLeft")} <Link to={routePaths.memberships}>{t("wizard.membership.seeMine")}</Link>
      </CAlert>
    );
  }

  const steps = [
    {
      label: t("wizard.steps.membership"),
      content: (
        <CCard>
          <CCardHeader>
            <h4 className="mb-0">{t("wizard.steps.membership")}</h4>
          </CCardHeader>
          <CCardBody className="d-flex flex-column gap-3">
            <p className="text-body-secondary mb-0">{t("wizard.membership.lead")}</p>
            <CRow xs={{ gutter: 3 }}>
              {available.map((candidate) => {
                const selected = candidate.id === tierId;

                return (
                  <CCol key={candidate.id} md={6}>
                    <button
                      type="button"
                      className={`card h-100 w-100 text-start p-3 d-flex flex-column gap-1${selected ? " border-primary" : ""}`}
                      aria-pressed={selected}
                      onClick={() => setTierId(candidate.id)}
                    >
                      <span className="fw-semibold d-flex align-items-center gap-2">
                        <MembershipBadge
                          membership={{
                            badgeColour: candidate.platform.badgeColour,
                            badgeLetters: candidate.platform.badgeLetters,
                            platformName: candidate.platform.name,
                          }}
                        />
                        {candidate.platform.name} · {candidate.name}
                      </span>
                      <span className="small text-body-secondary">{candidate.description}</span>
                      <span className="small text-body-secondary">
                        {t("browse.strategies", { count: countStrategiesByIds(candidate.tradingStrategyIds, catalogue) })}
                      </span>
                    </button>
                  </CCol>
                );
              })}
            </CRow>
            <div className="d-flex justify-content-between">
              <CButton color="secondary" variant="outline" onClick={() => navigate(routePaths.memberships)}>
                {t("wizard.cancel")}
              </CButton>
              <CButton color="primary" disabled={!tier} onClick={() => stepperRef.current?.next()}>
                {t("wizard.next")}
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      ),
    },
    {
      label: t("wizard.steps.account"),
      content: (
        <CCard>
          <CCardHeader>
            <h4 className="mb-0">{t("wizard.steps.account")}</h4>
          </CCardHeader>
          <CCardBody className="d-flex flex-column gap-3">
            {tier && (
              <div>
                <CFormLabel htmlFor="platform-account">{accountLabel}</CFormLabel>
                {locked ? (
                  <CInputGroup>
                    <CInputGroupText>
                      <CIcon icon={cilLockLocked} aria-hidden />
                    </CInputGroupText>
                    <CFormInput id="platform-account" value={account} disabled />
                  </CInputGroup>
                ) : (
                  <CFormInput
                    id="platform-account"
                    value={account}
                    maxLength={MAX_PLATFORM_ACCOUNT_LENGTH}
                    onChange={(e) => setTyped({ ...typed, [tier.platform.id]: e.target.value })}
                  />
                )}
                {offerSignInEmail && (
                  <div className="small mt-2">
                    {t("wizard.account.sameEmail", { platform: tier.platform.name })}{" "}
                    <CButton
                      color="link"
                      size="sm"
                      className="p-0 align-baseline"
                      onClick={() => setTyped({ ...typed, [tier.platform.id]: signInEmail })}
                    >
                      {t("wizard.account.useEmail", { email: signInEmail })}
                    </CButton>
                  </div>
                )}
                <div className="form-text">
                  {locked ? (
                    <>
                      {t("wizard.account.locked", { platform: tier.platform.name })}{" "}
                      <Link to={routePaths.contact}>{t("wizard.account.contact")}</Link>
                    </>
                  ) : (
                    t("wizard.account.help", { platform: tier.platform.name })
                  )}
                </div>
              </div>
            )}
            <div className="d-flex justify-content-between">
              <CButton color="secondary" variant="outline" onClick={() => stepperRef.current?.prev()}>
                {t("wizard.back")}
              </CButton>
              <CButton color="primary" disabled={!accountReady} onClick={() => stepperRef.current?.next()}>
                {t("wizard.next")}
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      ),
    },
    {
      label: t("wizard.steps.review"),
      content: (
        <CCard>
          <CCardHeader>
            <h4 className="mb-0">{t("wizard.steps.review")}</h4>
          </CCardHeader>
          <CCardBody className="d-flex flex-column gap-3">
            {tier && (
              <>
                <CListGroup>
                  <CListGroupItem className="d-flex justify-content-between gap-3 flex-wrap">
                    <span className="text-body-secondary">{t("wizard.review.membership")}</span>
                    <span className="fw-semibold">
                      {tier.platform.name} · {tier.name}
                    </span>
                  </CListGroupItem>
                  <CListGroupItem className="d-flex justify-content-between gap-3 flex-wrap">
                    <span className="text-body-secondary">{t("wizard.review.includes")}</span>
                    <span>{t("browse.strategies", { count: countStrategiesByIds(tier.tradingStrategyIds, catalogue) })}</span>
                  </CListGroupItem>
                  <CListGroupItem className="d-flex justify-content-between gap-3 flex-wrap">
                    <span className="text-body-secondary">{accountLabel}</span>
                    <span className="text-break">{trimmed}</span>
                  </CListGroupItem>
                </CListGroup>
                <div className="tinted-info rounded p-3">
                  {t("wizard.review.note", { tier: tier.name, platform: tier.platform.name })}
                </div>
              </>
            )}
            <div className="d-flex justify-content-between">
              <CButton
                color="secondary"
                variant="outline"
                onClick={() => stepperRef.current?.prev()}
                disabled={requestMembership.isPending}
              >
                {t("wizard.back")}
              </CButton>
              <CButton color="primary" onClick={handleSend} disabled={requestMembership.isPending}>
                {requestMembership.isPending && <CSpinner size="sm" className="me-2" />}
                {t("wizard.send")}
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      ),
    },
  ];

  return (
    <>
      <h2>{t("wizard.title")}</h2>
      <p className="text-body-secondary">{t("wizard.description")}</p>
      <hr />

      <CStepper
        ref={stepperRef}
        steps={steps}
        onStepChange={setCurrentStep}
        onReset={() => setCurrentStep(1)}
      />
    </>
  );
}

export default RequestMembershipWizard;
